import {
  InflationIndexItem,
  RetirementCountryPackItem,
  SavedWealthScenario,
  DeliverableP13,
} from '../types';

export const OFFICIAL_INFLATION_INDICES: InflationIndexItem[] = [
  {
    id: 'cpi-us-bls',
    countryCode: 'US',
    countryName: 'United States',
    currency: 'USD',
    indexName: 'US CPI-U (Consumer Price Index for All Urban Consumers)',
    source: 'US Bureau of Labor Statistics (BLS)',
    frequency: 'MONTHLY',
    latestPeriod: '2026-07',
    latestAnnualRatePct: 2.4,
    historical30yrAvgPct: 2.52,
    publishedAt: '2026-08-12',
    isOfficialSource: true,
  },
  {
    id: 'cpi-ca-statcan',
    countryCode: 'CA',
    countryName: 'Canada',
    currency: 'CAD',
    indexName: 'Canada All-items CPI',
    source: 'Statistics Canada (StatCan)',
    frequency: 'MONTHLY',
    latestPeriod: '2026-07',
    latestAnnualRatePct: 2.2,
    historical30yrAvgPct: 2.18,
    publishedAt: '2026-08-18',
    isOfficialSource: true,
  },
  {
    id: 'cpi-uk-ons',
    countryCode: 'GB',
    countryName: 'United Kingdom',
    currency: 'GBP',
    indexName: 'UK CPI (Consumer Prices Index)',
    source: 'UK Office for National Statistics (ONS)',
    frequency: 'MONTHLY',
    latestPeriod: '2026-07',
    latestAnnualRatePct: 2.1,
    historical30yrAvgPct: 2.45,
    publishedAt: '2026-08-19',
    isOfficialSource: true,
  },
  {
    id: 'hicp-eu-eurostat',
    countryCode: 'EU',
    countryName: 'Euro Area (EU20)',
    currency: 'EUR',
    indexName: 'Eurostat Harmonised Index of Consumer Prices (HICP)',
    source: 'Eurostat / European Central Bank',
    frequency: 'MONTHLY',
    latestPeriod: '2026-07',
    latestAnnualRatePct: 2.0,
    historical30yrAvgPct: 2.08,
    publishedAt: '2026-08-18',
    isOfficialSource: true,
  },
  {
    id: 'cpi-au-abs',
    countryCode: 'AU',
    countryName: 'Australia',
    currency: 'AUD',
    indexName: 'Australia Headline CPI',
    source: 'Australian Bureau of Statistics (ABS)',
    frequency: 'MONTHLY',
    latestPeriod: '2026-07',
    latestAnnualRatePct: 2.6,
    historical30yrAvgPct: 2.65,
    publishedAt: '2026-08-20',
    isOfficialSource: true,
  },
  {
    id: 'cpi-jp-stat',
    countryCode: 'JP',
    countryName: 'Japan',
    currency: 'JPY',
    indexName: 'Japan CPI (All Items ex. Fresh Food)',
    source: 'Statistics Bureau of Japan / Ministry of Internal Affairs',
    frequency: 'MONTHLY',
    latestPeriod: '2026-07',
    latestAnnualRatePct: 1.8,
    historical30yrAvgPct: 0.62,
    publishedAt: '2026-08-21',
    isOfficialSource: true,
  },
  {
    id: 'cpi-cm-ins',
    countryCode: 'CM',
    countryName: 'Cameroon',
    currency: 'XAF',
    indexName: 'Cameroon National Consumer Price Index (IHPC)',
    source: 'Institut National de la Statistique (INS Cameroun)',
    frequency: 'MONTHLY',
    latestPeriod: '2026-06',
    latestAnnualRatePct: 3.5,
    historical30yrAvgPct: 3.2,
    publishedAt: '2026-07-30',
    isOfficialSource: true,
  },
];

export const RETIREMENT_COUNTRY_PACKS: RetirementCountryPackItem[] = [
  {
    id: 'pack-us-2026',
    countryCode: 'US',
    countryName: 'United States',
    capabilityLevel: 'REGIONAL_RULES_COMPLETE',
    taxYear: '2026',
    supportedAccounts: [
      {
        code: '401K',
        name: '401(k) / 403(b) Workplace Retirement',
        taxTreatment: 'TAX_DEFERRED',
        annualContributionLimit: 23500,
        catchUpLimit: 7500,
        limitSource: 'IRS Notice 2025-83 (2026 Cost-of-Living Adjustments)',
        employerMatchSupported: true,
      },
      {
        code: 'ROTH_IRA',
        name: 'Roth IRA (Tax-Free Growth)',
        taxTreatment: 'TAX_FREE',
        annualContributionLimit: 7000,
        catchUpLimit: 1000,
        limitSource: 'IRS Publication 590-A',
        employerMatchSupported: false,
      },
      {
        code: 'TRAD_IRA',
        name: 'Traditional IRA (Tax-Deductible)',
        taxTreatment: 'TAX_DEFERRED',
        annualContributionLimit: 7000,
        catchUpLimit: 1000,
        limitSource: 'IRS Publication 590-A',
        employerMatchSupported: false,
      },
    ],
    officialSources: ['Internal Revenue Service (IRS)', 'U.S. Department of Labor'],
    lastReviewedAt: '2026-01-15',
  },
  {
    id: 'pack-ca-2026',
    countryCode: 'CA',
    countryName: 'Canada',
    capabilityLevel: 'REGIONAL_RULES_COMPLETE',
    taxYear: '2026',
    supportedAccounts: [
      {
        code: 'RRSP',
        name: 'Registered Retirement Savings Plan (RRSP)',
        taxTreatment: 'TAX_DEFERRED',
        annualContributionLimit: 32490,
        limitSource: 'Canada Revenue Agency (CRA) Indexation Schedule',
        employerMatchSupported: true,
      },
      {
        code: 'TFSA',
        name: 'Tax-Free Savings Account (TFSA)',
        taxTreatment: 'TAX_FREE',
        annualContributionLimit: 7000,
        limitSource: 'Canada Revenue Agency (CRA)',
        employerMatchSupported: false,
      },
    ],
    officialSources: ['Canada Revenue Agency (CRA)', 'Department of Finance Canada'],
    lastReviewedAt: '2026-01-10',
  },
  {
    id: 'pack-uk-2026',
    countryCode: 'GB',
    countryName: 'United Kingdom',
    capabilityLevel: 'STANDARD',
    taxYear: '2026-2027',
    supportedAccounts: [
      {
        code: 'SIPP',
        name: 'Self-Invested Personal Pension (SIPP)',
        taxTreatment: 'TAX_DEFERRED',
        annualContributionLimit: 60000,
        limitSource: 'HM Revenue & Customs (HMRC) Pensions Tax Manual',
        employerMatchSupported: false,
      },
      {
        code: 'STOCKS_ISA',
        name: 'Stocks & Shares ISA',
        taxTreatment: 'TAX_FREE',
        annualContributionLimit: 20000,
        limitSource: 'HM Revenue & Customs (HMRC)',
        employerMatchSupported: false,
      },
    ],
    officialSources: ['HM Revenue & Customs (HMRC)', 'The Pensions Regulator'],
    lastReviewedAt: '2026-04-06',
  },
  {
    id: 'pack-au-2026',
    countryCode: 'AU',
    countryName: 'Australia',
    capabilityLevel: 'STANDARD',
    taxYear: '2026-2027',
    supportedAccounts: [
      {
        code: 'SUPER',
        name: 'Concessional Superannuation',
        taxTreatment: 'TAX_DEFERRED',
        annualContributionLimit: 30000,
        limitSource: 'Australian Taxation Office (ATO)',
        employerMatchSupported: true,
      },
    ],
    officialSources: ['Australian Taxation Office (ATO)', 'APRA'],
    lastReviewedAt: '2026-07-01',
  },
];

export const INITIAL_PRESET_SCENARIOS: SavedWealthScenario[] = [
  {
    id: 'scen-growth-20yr',
    title: '20-Year Wealth Accumulation ($500/mo @ 7%)',
    category: 'COMPOUND',
    currency: 'USD',
    inputs: {
      initialPrincipal: 10000,
      periodicContribution: 500,
      nominalReturnRatePct: 7.0,
      inflationRatePct: 2.5,
      annualFeePct: 0.25,
      timeHorizonYears: 20,
      compoundingFrequency: 'monthly',
      contributionFrequency: 'monthly',
      contributionTiming: 'END_OF_PERIOD',
      currency: 'USD',
    },
    result: {
      formulaKey: 'compound_annuity',
      formulaVersion: 'v1.0.0-P13-DETERMINISTIC',
      nominalFutureValue: 289132.42,
      realFutureValue: 176449.12,
      totalPrincipalInvested: 10000,
      totalContributions: 130000,
      totalGrowth: 159132.42,
      totalFeesDeducted: 7812.3,
      lostGrowthDueToFees: 12450.8,
      effectiveRealReturnPct: 4.15,
      purchasingPowerErosionPct: 38.9,
      yearlyBreakdown: [],
      formulaTrace: [],
      assumptionsSummary: {
        nominalReturnRate: '7.0% annual gross',
        inflationRate: '2.5% user assumption',
        feeRate: '0.25% annual fee',
        timeHorizon: '20 years',
        timing: 'End of Period',
        sourceLabel: 'YamooPay Deterministic Universal Math Core',
      },
    },
    createdAt: '2026-08-24T12:00:00Z',
    isPrivate: true,
  },
  {
    id: 'scen-fire-40k',
    title: 'FIRE $1M Target ($40k Spending @ 4% Rule)',
    category: 'FIRE',
    currency: 'USD',
    inputs: {
      initialPrincipal: 50000,
      periodicContribution: 1500,
      nominalReturnRatePct: 8.0,
      inflationRatePct: 2.5,
      annualFeePct: 0.15,
      timeHorizonYears: 18,
      compoundingFrequency: 'monthly',
      contributionFrequency: 'monthly',
      contributionTiming: 'END_OF_PERIOD',
      annualRetirementSpending: 40000,
      withdrawalRatePct: 4.0,
      currency: 'USD',
    },
    result: {
      formulaKey: 'fire_target',
      formulaVersion: 'v1.0.0-P13-DETERMINISTIC',
      nominalFutureValue: 1012480.0,
      realFutureValue: 649200.0,
      totalPrincipalInvested: 50000,
      totalContributions: 374000,
      totalGrowth: 638480.0,
      totalFeesDeducted: 14200.0,
      lostGrowthDueToFees: 21500.0,
      effectiveRealReturnPct: 5.22,
      purchasingPowerErosionPct: 35.9,
      fireTargetAmount: 1000000,
      fireYearsToTarget: 17.8,
      retirementSustainabilityYears: 32,
      yearlyBreakdown: [],
      formulaTrace: [],
      assumptionsSummary: {
        nominalReturnRate: '8.0% annual gross',
        inflationRate: '2.5% user assumption',
        feeRate: '0.15% annual fee',
        timeHorizon: '18 years',
        timing: 'End of Period',
        sourceLabel: 'YamooPay Deterministic Universal Math Core',
      },
    },
    createdAt: '2026-08-24T12:00:00Z',
    isPrivate: true,
  },
];

export const DELIVERABLES_P13: DeliverableP13[] = [
  {
    code: 'P13-01',
    titleEn: 'YamooPay Universal Wealth Mathematics Engine',
    titleFr: 'Moteur Universel de Mathématiques Financières YamooPay',
    category: 'Universal Wealth Mathematics Engine',
    summary: 'Calculs déterministes de capitalisation, valeur future, valeur présente, versements périodiques, frais et formule multiplicative exacte du rendement réel.',
    details: [
      'Gère les fréquences quotidiennes, mensuelles, trimestrielles et annuelles.',
      'Distingue le versement en début de période (Annuity Due) du versement en fin de période (Ordinary Annuity).',
      'Intègre la formule exacte du rendement réel : ((1 + r_nominal) / (1 + inflation)) - 1.',
      'Affiche un tracé explicatif pas-à-pas (Formula Trace) sans boîte noire.',
    ],
    architectureNotes: 'Écrit en pure TypeScript déterministe dans src/lib/wealthMathEngine.ts.',
  },
  {
    code: 'P13-02',
    titleEn: 'Global Inflation & Purchasing Power Intelligence Engine',
    titleFr: 'Moteur d\'Intelligence de l\'Inflation & Pouvoir d\'Achat Mondial',
    category: 'Inflation & Purchasing Power Intelligence',
    summary: 'Données statistiques officielles d\'inflation (BLS, StatCan, ONS, Eurostat, ABS) et modélisation de l\'érosion du pouvoir d\'achat.',
    details: [
      'Sépare strictement l\'inflation historique constatée de l\'hypothèse d\'inflation future.',
      'Calcule la valeur équivalente en dollars/euros d\'aujourd\'hui.',
      'Fournit un tableau de suivi des sources officielles avec fréquence et dernière période publiée.',
      'Garde-fou : ne confond jamais l\'érosion du pouvoir d\'achat locale avec les taux de change FX.',
    ],
    architectureNotes: 'Architecture versionnée alimentée par OFFICIAL_INFLATION_INDICES.',
  },
  {
    code: 'P13-03',
    titleEn: 'Financial Goal Planning & Solver Engine',
    titleFr: 'Solveur de Projets Financiers & Épargne Requise',
    category: 'Financial Goal Planning & Solver Engine',
    summary: 'Résolution déterministe de l\'épargne mensuelle requise pour atteindre un objectif ou de la durée nécessaire.',
    details: [
      'Calcule l\'effort d\'épargne mensuel exact selon l\'horizon et le rendement cible.',
      'Résout la durée exacte en années si les versements actuels sont insuffisants.',
      'Détecte les configurations impossibles sans divergence ni plantage.',
      'Affiche le pourcentage d\'atteinte de l\'objectif.',
    ],
    architectureNotes: 'Exécuté via calculateGoalSolver() dans wealthMathEngine.ts.',
  },
  {
    code: 'P13-04',
    titleEn: 'FIRE & Retirement Projection Engine (Accumulation & Drawdown)',
    titleFr: 'Moteur de Retraite & Indépendance Financière FIRE',
    category: 'FIRE & Retirement Projection Engine',
    summary: 'Modélisation des deux phases (Accumulation & Décaisse) avec règles heuristiques 4% / 25x et durabilité des retraits ajustés de l\'inflation.',
    details: [
      'Affiche le montant cible FIRE et l\'horizon estimé sous hypothèses.',
      'Simule l\'épuisement progressif du capital lors de la phase de retraite (Decumulation).',
      'Langage de confiance : qualifie le taux de retrait d\'hypothèse illustrative et non de garantie.',
      'Packs pays pour 401(k), Roth IRA, RRSP, TFSA, SIPP et Superannuation.',
    ],
    architectureNotes: 'Packs réglementaires configurés via RETIREMENT_COUNTRY_PACKS.',
  },
  {
    code: 'P13-05',
    titleEn: 'Multi-Scenario Comparison & Long-Term Fee Impact',
    titleFr: 'Comparateur de Scénarios & Impact des Frais d\'Investissement',
    category: 'Scenario Comparison & Sensitivity Analysis',
    summary: 'Comparaison simultanée de 2 à 4 scénarios d\'investissement et calcul de la perte de croissance due aux frais de gestion.',
    details: [
      'Compare le scénario A ($500/mois à 7%) vs Scénario B ($750/mois à 6%).',
      'Isole la croissance perdue attribuable aux frais d\'administration (Lost Growth Due to Fees).',
      'Analyse de sensibilité : teste l\'effet de ±1% de rendement ou d\'inflation.',
      'Visualisations claires (cartes & tableaux alternatifs) accessibles WCAG 2.2 AA.',
    ],
    architectureNotes: 'Fonctionne sans compte utilisateur tout en permettant la sauvegarde locale.',
  },
  {
    code: 'P13-06',
    titleEn: 'YamooPay Wealth Intelligence Command Center',
    titleFr: 'Centre de Commandement de l\'Intelligence Patrimoniale YamooPay',
    category: 'Wealth Intelligence Command Center',
    summary: 'Pilotage exécutif des calculateurs de richesse, gouvernance des indices d\'inflation, matrice des packs retraite pays, registre des formules et santé du moteur.',
    details: [
      'Statistiques d\'utilisation anonymes et respectueuses de la vie privée (aucun solde privé transmis).',
      'Suivi de la fraîcheur des données d\'inflation et de la validité des limites réglementaires.',
      'Registre public des versions de formules et traçabilité mathématique.',
      'Kill-switch par calculateur et gestion d\'incidents de données.',
    ],
    architectureNotes: 'Composant dédié React dans src/components/wealth/WealthCommandCenter.tsx.',
  },
];
